/**
 * 与“真实物理尺寸”有关的纯数学逻辑。
 *
 * 这里完全不依赖 React、Canvas 或 PDF，因此可以独立单元测试。
 * 所有核心尺寸都统一使用 mm；只有图片天然尺寸保留 px。
 */

export type Orientation = 'portrait' | 'landscape'

export interface GridPreset {
  id: string
  label: string
  description: string
  cols: number
  rows: number
  cellMm: number
}

export interface Tile {
  index: number
  col: number
  row: number
  xMm: number
  yMm: number
  widthMm: number
  heightMm: number
}

export interface TilePlan {
  pageWidthMm: number
  pageHeightMm: number
  printableWidthMm: number
  printableHeightMm: number
  stepXmm: number
  stepYmm: number
  columns: number
  rows: number
  tiles: Tile[]
}

export interface ImagePlacement {
  leftMm: number
  topMm: number
  widthMm: number
  heightMm: number
}

export const GRID_PRESETS: GridPreset[] = [
  {
    id: 'beads-mini-78',
    label: '2.6 mm 拼豆 · 78×78',
    description: '你现在这块透明中板',
    cols: 78,
    rows: 78,
    cellMm: 2.6,
  },
  {
    id: 'beads-mini-52',
    label: '2.6 mm 拼豆 · 52×52',
    description: '较小透明板',
    cols: 52,
    rows: 52,
    cellMm: 2.6,
  },
  {
    id: 'battlemap-1in',
    label: '跑团地图 · 1 英寸格',
    description: 'D&D / Pathfinder 常见实体格距',
    cols: 20,
    rows: 15,
    cellMm: 25.4,
  },
]

export function getPaperSizeMm(orientation: Orientation) {
  return orientation === 'portrait'
    ? { widthMm: 210, heightMm: 297 }
    : { widthMm: 297, heightMm: 210 }
}

/**
 * 计算最终成品格区尺寸。
 * 这里把“78×78”理解为 78 个单元格，每格宽 2.6 mm，所以总宽为 202.8 mm。
 * 对透明拼豆板而言，珠子/钉位应落在格子的中心位置。
 */
export function getPosterSizeMm(cols: number, rows: number, cellMm: number) {
  return {
    widthMm: cols * cellMm,
    heightMm: rows * cellMm,
  }
}

/**
 * 生成分页方案。
 * 每张纸都有安全页边距；相邻页可以保留 overlapMm 的重复内容，方便裁切拼接。
 */
export function calculateTilePlan(params: {
  posterWidthMm: number
  posterHeightMm: number
  orientation: Orientation
  marginMm: number
  overlapMm: number
}): TilePlan {
  const { posterWidthMm, posterHeightMm, orientation, marginMm, overlapMm } = params
  const page = getPaperSizeMm(orientation)
  const printableWidthMm = page.widthMm - marginMm * 2
  const printableHeightMm = page.heightMm - marginMm * 2

  if (posterWidthMm <= 0 || posterHeightMm <= 0) {
    throw new Error('成品尺寸必须大于 0')
  }
  if (printableWidthMm <= 0 || printableHeightMm <= 0) {
    throw new Error('页边距过大，已经没有可打印区域')
  }
  if (overlapMm < 0 || overlapMm >= printableWidthMm || overlapMm >= printableHeightMm) {
    throw new Error('分页重叠必须小于单页可打印区域')
  }

  const stepXmm = printableWidthMm - overlapMm
  const stepYmm = printableHeightMm - overlapMm

  const xs: number[] = []
  const ys: number[] = []

  for (let x = 0; ; x += stepXmm) {
    xs.push(x)
    if (x + printableWidthMm >= posterWidthMm - 1e-9) break
  }

  for (let y = 0; ; y += stepYmm) {
    ys.push(y)
    if (y + printableHeightMm >= posterHeightMm - 1e-9) break
  }

  const tiles: Tile[] = []
  let index = 0
  ys.forEach((yMm, row) => {
    xs.forEach((xMm, col) => {
      tiles.push({
        index: index++,
        col,
        row,
        xMm,
        yMm,
        widthMm: Math.min(printableWidthMm, posterWidthMm - xMm),
        heightMm: Math.min(printableHeightMm, posterHeightMm - yMm),
      })
    })
  })

  return {
    pageWidthMm: page.widthMm,
    pageHeightMm: page.heightMm,
    printableWidthMm,
    printableHeightMm,
    stepXmm,
    stepYmm,
    columns: xs.length,
    rows: ys.length,
    tiles,
  }
}

/**
 * 以“覆盖整个格区（cover）”为初始策略，把图片映射到毫米坐标。
 * zoom=1 时至少铺满整个格区；offsetX/Y 用 mm 表示平移，后续导出可直接复用。
 */
export function getImagePlacement(params: {
  imageWidthPx: number
  imageHeightPx: number
  posterWidthMm: number
  posterHeightMm: number
  zoom: number
  offsetXmm: number
  offsetYmm: number
}): ImagePlacement {
  const {
    imageWidthPx,
    imageHeightPx,
    posterWidthMm,
    posterHeightMm,
    zoom,
    offsetXmm,
    offsetYmm,
  } = params

  if (imageWidthPx <= 0 || imageHeightPx <= 0) {
    throw new Error('图片尺寸无效')
  }

  const coverScaleMmPerPx = Math.max(
    posterWidthMm / imageWidthPx,
    posterHeightMm / imageHeightPx,
  )
  const widthMm = imageWidthPx * coverScaleMmPerPx * zoom
  const heightMm = imageHeightPx * coverScaleMmPerPx * zoom

  return {
    leftMm: (posterWidthMm - widthMm) / 2 + offsetXmm,
    topMm: (posterHeightMm - heightMm) / 2 + offsetYmm,
    widthMm,
    heightMm,
  }
}

/**
 * 用户打印 100 mm 校准线后输入“实际量到多少 mm”，据此反推补偿系数。
 * 例如实际只有 99.3 mm，则输出尺寸应乘以 100/99.3 ≈ 1.00705。
 */
export function getCalibrationFactor(measured100Mm: number) {
  if (!Number.isFinite(measured100Mm) || measured100Mm <= 0) return 1
  return 100 / measured100Mm
}
